import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { MfaStep } from "@/components/MfaStep";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, Car, Bike, TrendingUp } from "lucide-react";
import { InstallApp } from "@/components/InstallApp";

const homePath = () => (window.matchMedia("(display-mode: standalone)").matches || window.innerWidth < 768 ? "/workspace" : "/");

export default function Login({ portal = "staff" }) {
  const isAdmin = portal === "admin";
  const { login, completeLogin, user } = useAuth();
  const [mfa, setMfa] = useState(null);
  const [reason] = useState(() => { const r = sessionStorage.getItem("omi-logout-reason"); sessionStorage.removeItem("omi-logout-reason"); return r; });
  const nav = useNavigate();
  const [eid, setEid] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const data = await login(eid, pw, portal);
      if (data.mfa_required) { setMfa(data); return; }
      nav(homePath(), { replace: true });
    } catch (ex) {
      setErr(apiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };

  if (user && !user.must_change_password) return <Navigate to={homePath()} replace />;
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm fade-in">
          <div className="flex justify-end mb-3"><InstallApp label="Install app" /></div>
          <div className="mb-8">
            <div className="inline-block bg-white rounded-xl px-3 py-2 border border-slate-200 shadow-sm"><img src="/omi-logo.webp" alt="OMI Fintech Pvt Ltd" className="h-20 w-auto object-contain" data-testid="login-logo" /></div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-2">Insurance + Loan CRM</div>
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight" data-testid="login-heading">{isAdmin ? "Super Admin sign in" : "Welcome back"}</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-7">{isAdmin ? "Restricted page. 2-step code required after your password." : "Sign in with your Employee ID to continue."}</p>

          {reason && !mfa && <div className="mb-4 text-sm rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 px-3 py-2" data-testid="login-idle-message">{reason}</div>}
          {mfa ? <MfaStep mfa={mfa} onDone={(d) => { completeLogin(d); nav(homePath(), { replace: true }); }} onBack={() => setMfa(null)} /> : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="eid" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Employee ID</Label>
              <Input id="eid" value={eid} onChange={(e) => setEid(e.target.value)} placeholder="e.g. OMI-EMP-401"
                className="mt-1.5 font-mono uppercase" autoFocus data-testid="login-employee-id-input" />
            </div>
            <div>
              <Label htmlFor="pw" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Password</Label>
              <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••"
                className="mt-1.5" data-testid="login-password-input" />
            </div>
            {portal !== "admin" && <div className="text-right -mt-2"><Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="login-forgot-password-link">Forgot password?</Link></div>}
            {err && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}
            <Button type="submit" className="w-full h-11 text-sm font-semibold" disabled={loading} data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>)}

        </div>
      </div>

      {/* Right: brand panel */}
      <div className="hidden lg:flex relative flex-col justify-between bg-slate-900 p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #2563EB 0, transparent 45%), radial-gradient(circle at 80% 70%, #1E3A8A 0, transparent 40%)" }} />
        <div className="relative z-10 flex items-center gap-2 text-white/70 text-sm">
          <ShieldCheck className="h-5 w-5" /> Pan-India Insurance Operations
        </div>
        <div className="relative z-10">
          <h2 className="text-white font-heading font-extrabold text-4xl leading-tight">One system to run your entire insurance business.</h2>
          <p className="text-slate-300 mt-4 max-w-md text-[15px] leading-relaxed">
            Leads, Customer 360, quotes, policies, renewals, commissions and accounts — built for telecallers, RMs and management.
          </p>
          <div className="flex gap-4 mt-8">
            {[[Car, "Motor"], [Bike, "Two-Wheeler"], [TrendingUp, "Renewals MIS"]].map(([Icon, label], i) => (
              <div key={i} className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg px-4 py-2.5 text-white text-sm">
                <Icon className="h-4 w-4" /> {label}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-slate-400 text-xs">© 2026 OMI Fintech Pvt Ltd. All data secured & role-based.</div>
      </div>
    </div>
  );
}
